import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Logo from "../../../icons/Logo";
import { Button } from "../../../shadcn/components/ui/button";
import SpinLoader from "../../buyer/SpinLoader";
import { useDispatch, useSelector } from "react-redux";
import { clearSellerAuth, setSellerTeams } from "store";
import { DateTime } from "luxon";
import { verifyTeamMemberRequest } from "services";

type AccessState = "loading" | "valid" | "invalid";

export default function SellerValidationWrapper(props: any) {
  const [userAccessValid, setUserAccessValid] =
    useState<AccessState>("loading");
  const router = useRouter();
  const dispatch = useDispatch();

  const { user, accessToken, accessType, loginDate } = useSelector(
    (state: any) => state.sellerAuth,
  );

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const pathname = router.pathname ?? "";
      const isLoginPage = pathname === "/login";

      // Not logged in: only gate protected routes; always allow /login
      if (!accessToken) {
        if (!isLoginPage) {
          dispatch(clearSellerAuth());
          router.replace("/login");
          // Stay on loader until pathname is /login — avoids flashing homepage
          if (!cancelled) setUserAccessValid("loading");
          return;
        }
        if (!cancelled) setUserAccessValid("valid");
        return;
      }

      // Logged in but daily session expired
      const todayIST = DateTime.now()
        .setZone("Asia/Kolkata")
        .toFormat("yyyy-MM-dd");
      if (loginDate && loginDate !== todayIST) {
        dispatch(clearSellerAuth());
        router.replace("/login");
        if (!cancelled) setUserAccessValid("loading");
        return;
      }

      // From here on: session is active — re-check access + teams (runs on load & navigation)
      if (!cancelled) setUserAccessValid("loading");

      if (!accessType || accessType !== "seller") {
        if (!cancelled) setUserAccessValid("invalid");
        return;
      }

      if (!user?.uuid) {
        if (!cancelled) setUserAccessValid("invalid");
        return;
      }

      try {
        const result: any = await verifyTeamMemberRequest({
          user_uuid: user.uuid,
        });
        // const result = {
        //   valid: true,
        //   teams: [
        //     {
        //       uuid: "edd7347d-5d10-4ede-9164-d838e1d5c414",
        //       creator_user_uuid: null,
        //       name: "1553a439-f2dd-4605-9340-ddfbac903b30",
        //       slug: "1553a439-f2dd-4605-9340-ddfbac903b30",
        //       tenant: "swiss-asset",
        //       meta: {
        //         name: "1553a439-f2dd-4605-9340-ddfbac903b30",
        //         type: "swiss-asset",
        //         asset_data: {
        //           uuid: "1553a439-f2dd-4605-9340-ddfbac903b30",
        //           status: "draft",
        //           dpd_days: 92,
        //           npa_date: "2025-11-15T00:00:00.000Z",
        //           created_at: "2026-04-13T13:17:04.460Z",
        //           created_by: "admin",
        //           deleted_at: null,
        //           listing_id: "ABSW-26-000001",
        //           updated_at: "2026-04-13T13:17:04.460Z",
        //           legal_status: "notice_issued",
        //           borrower_name: "Ravi Kumar",
        //           security_type: "residential_property",
        //           valuation_date: "2026-03-20T00:00:00.000Z",
        //           sanction_amount: "25000000",
        //           data_cutoff_date: "2026-03-31T00:00:00.000Z",
        //           valuation_amount: "22000000.50",
        //           litigation_status: "none",
        //           portfolio_segment: "retail_mortgage",
        //           total_outstanding: "19875000",
        //           number_of_accounts: 1,
        //           geographical_spread: "NCR",
        //           loan_account_number: "LAN-2026-000145",
        //           borrower_entity_type: "individual",
        //           aggregate_outstanding: "19875000.75",
        //           outstanding_principal: "18250000",
        //           secured_asset_location: "Sector 62, Noida, Uttar Pradesh",
        //           asset_transfer_event_id: null,
        //           secured_asset_description: "3BHK apartment with parking",
        //         },
        //         bashBoardUrl: "https://sat.quickbid.in/",
        //       },
        //       created_at: "2026-04-13T13:17:05.983Z",
        //       updated_at: "2026-04-13T13:17:05.983Z",
        //       membership_uuid: "6c8133f6-470e-4563-8bb3-327cb5f03e14",
        //       membership_permissions: {
        //         role: "approver",
        //       },
        //     },
        //   ],
        // };
        if (cancelled) return;

        const teamsOk =
          result?.valid === true &&
          Array.isArray(result?.teams) &&
          result.teams.length > 0;

        if (teamsOk) {
          dispatch(setSellerTeams(result.teams));
          setUserAccessValid("valid");
        } else {
          setUserAccessValid("invalid");
        }
      } catch (e) {
        console.error("Seller team verification failed:", e);
        if (!cancelled) setUserAccessValid("invalid");
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // router methods/replace used inside effect; pathname + isReady cover updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    accessType,
    loginDate,
    user?.uuid,
    dispatch,
    router.isReady,
    router.pathname,
  ]);

  const fullScreenSpinner = (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F8F8F8] px-4 py-8">
      <SpinLoader />
    </div>
  );

  // Router not hydrated yet, or auth/team verification in progress
  if (!router.isReady || userAccessValid === "loading") {
    return fullScreenSpinner;
  }
  if (userAccessValid === "invalid") {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F8F8F8] px-4 py-8">
        <div className="w-full max-w-2xl rounded-lg border border-[#F4D4CF] bg-brand-lightPink shadow-sm">
          <div className="flex flex-col items-center px-6 py-7">
            <div className="inline-block mb-5 tracking-wider">
              <Logo />
            </div>
            <div className="inline-flex items-center rounded-full bg-[#FFF3F3] px-3 py-1 text-[11px] font-semibold text-[#B60100]">
              Access Restricted
            </div>
            <h1 className="mt-4 text-xl text-center font-semibold text-brand-textBlack">
              Seller access not available
            </h1>
            <div className="mt-2 text-center text-sm text-brand-textGrey">
              You are trying to access a portal that is not assigned to your
              account. Please verify your access type and sign in again.
            </div>
            <div className="mt-6 w-full">
              <Button
                className="w-full"
                onClick={() => {
                  setUserAccessValid("loading");
                  dispatch(clearSellerAuth());
                  router.replace("/login");
                }}
              >
                Back to login
              </Button>
            </div>
          </div>
          <div className="border-t border-[#F2F2F2] px-6 py-3 text-center text-xs text-brand-textLightGrey">
            If this is unexpected, contact support.
          </div>
        </div>
      </div>
    );
  }

  if (userAccessValid === "valid") {
    return props.children;
  }

  return null;
}
